/**
 * BillingAlertStrip - At-a-glance alert summary for the billing overview
 *
 * Three clickable cards surfacing the most actionable signals:
 * - Free plan accounts (conversion opportunity)
 * - Total past-due amount (revenue at risk)
 * - Disputes needing response (urgent action)
 */
import { motion } from 'framer-motion';
import { AlertCircle, AlertTriangle, ArrowRight, UserX } from 'lucide-react';
import { cn } from '@respectly/ui';

// ============================================
// Types
// ============================================

interface BillingAlertStripProps {
  freePlanCount: number;
  pastDueTotalAmount: number;
  disputesNeedingResponseCount: number;
  onNavigateToPlans: () => void;
  onNavigateToPastDue: () => void;
  onNavigateToDisputes: () => void;
}

// ============================================
// Formatters
// ============================================

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const numberFormatter = new Intl.NumberFormat('en-US');

// ============================================
// Alert Card Sub-component
// ============================================

interface AlertCardProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  sublabel: string;
  borderColor: string;
  iconBg: string;
  iconColor: string;
  valueColor: string;
  onClick: () => void;
}

function AlertCard({
  icon,
  value,
  label,
  sublabel,
  borderColor,
  iconBg,
  iconColor,
  valueColor,
  onClick,
}: AlertCardProps) {
  return (
    <motion.button
      whileHover={{ y: -2, scale: 1.005 }}
      whileTap={{ scale: 0.998 }}
      transition={{ duration: 0.15 }}
      onClick={onClick}
      className={cn(
        'relative w-full text-left rounded-xl border bg-white dark:bg-slate-900',
        'shadow-sm hover:shadow-md transition-shadow duration-200',
        'overflow-hidden cursor-pointer group',
        'border-slate-200/80 dark:border-slate-700/80'
      )}
    >
      {/* Colored left accent bar */}
      <div className={cn('absolute inset-y-0 left-0 w-1 rounded-l-xl', borderColor)} />

      <div className="pl-5 pr-4 py-4 flex items-center gap-4">
        {/* Icon */}
        <div className={cn('flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center', iconBg)}>
          <div className={iconColor}>{icon}</div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={cn('text-2xl font-bold tabular-nums tracking-tight leading-none mb-0.5', valueColor)}>
            {value}
          </p>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200 leading-tight">
            {label}
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 leading-tight">
            {sublabel}
          </p>
        </div>

        {/* Arrow */}
        <ArrowRight className="h-4 w-4 text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:group-hover:text-slate-400 group-hover:translate-x-0.5 transition-all duration-150 flex-shrink-0" />
      </div>
    </motion.button>
  );
}

// ============================================
// Component
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

export function BillingAlertStrip({
  freePlanCount,
  pastDueTotalAmount,
  disputesNeedingResponseCount,
  onNavigateToPlans,
  onNavigateToPastDue,
  onNavigateToDisputes,
}: BillingAlertStripProps) {
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid gap-4 sm:grid-cols-3"
    >
      {/* Free Plan Accounts */}
      <motion.div variants={itemVariants}>
        <AlertCard
          icon={<UserX className="h-5 w-5" />}
          value={numberFormatter.format(freePlanCount)}
          label="Free Plan Accounts"
          sublabel="Conversion opportunities"
          borderColor="bg-amber-400 dark:bg-amber-500"
          iconBg="bg-amber-50 dark:bg-amber-900/25"
          iconColor="text-amber-600 dark:text-amber-400"
          valueColor="text-amber-700 dark:text-amber-400"
          onClick={onNavigateToPlans}
        />
      </motion.div>

      {/* Past Due */}
      <motion.div variants={itemVariants}>
        <AlertCard
          icon={<AlertCircle className="h-5 w-5" />}
          value={pastDueTotalAmount > 0 ? currencyFormatter.format(pastDueTotalAmount) : '$0'}
          label="Total Past Due"
          sublabel="Revenue at risk"
          borderColor="bg-red-400 dark:bg-red-500"
          iconBg="bg-red-50 dark:bg-red-900/25"
          iconColor="text-red-600 dark:text-red-400"
          valueColor="text-red-700 dark:text-red-400"
          onClick={onNavigateToPastDue}
        />
      </motion.div>

      {/* Disputes Needing Response */}
      <motion.div variants={itemVariants}>
        <AlertCard
          icon={<AlertTriangle className="h-5 w-5" />}
          value={numberFormatter.format(disputesNeedingResponseCount)}
          label="Disputes Pending"
          sublabel="Require your response"
          borderColor="bg-orange-400 dark:bg-orange-500"
          iconBg="bg-orange-50 dark:bg-orange-900/25"
          iconColor="text-orange-600 dark:text-orange-400"
          valueColor="text-orange-700 dark:text-orange-400"
          onClick={onNavigateToDisputes}
        />
      </motion.div>
    </motion.div>
  );
}
